import type { FulfilmentIndexData, FulfilmentIndexPlaceClimber, FulfilmentIndexSkuClimber } from "./api";

const cell = (value: unknown) => {
  const text = value == null ? "" : String(value);
  return /[",\r\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
};
const csv = (columns: readonly string[], rows: Array<Record<string, unknown>>) => `${columns.join(",")}\r\n${rows.map((row) => columns.map((column) => cell(row[column])).join(",")).join("\r\n")}\r\n`;

export const FULFILMENT_INDEX_RANKING_COLUMNS = ["sku", "tracking", "variant", "place_id", "place_label", "channel", "region", "country", "key", "label", "base_unit_cost", "latest_unit_cost", "delta_abs", "delta_pct", "top_contributing_line", "units_sold_window", "impact_window", "carried_since", "native_currency", "base_unit_cost_native", "latest_unit_cost_native", "lane_label", "currency"] as const;
export const FULFILMENT_INDEX_SERIES_COLUMNS = ["month", "line_key", "label", "unit_cost", "index", "total_unit_cost", "total_index", "data_quality", "carried_sku_count", "reference_inflation_pct", "currency"] as const;

export type FulfilmentRankingKind = "skus" | "places" | "lines" | "lanes";

export function fulfilmentIndexRankingCsv(data: FulfilmentIndexData, kind: FulfilmentRankingKind): string {
  const rows = kind === "skus" ? data.sku_climbers : kind === "places" ? data.place_climbers : kind === "lanes" ? data.lane_climbers ?? [] : data.climbers;
  return csv(FULFILMENT_INDEX_RANKING_COLUMNS, rows.map((row) => ({
    ...row,
    top_contributing_line: row.top_contributing_line?.label ?? "",
    currency: data.scope.presentation_currency,
  })) as unknown as Array<Record<string, unknown>>);
}

/** Every Fulfilment Index CSV ends with the presentation currency its money columns are in (owner decision 4, v1.1.1). */
export function fulfilmentIndexSeriesCsv(data: FulfilmentIndexData): string {
  const inflation = data.reference_inflation?.cumulative_pct ?? "";
  const currency = data.scope.presentation_currency;
  return csv(FULFILMENT_INDEX_SERIES_COLUMNS, data.series.flatMap((point) => point.lines.length ? point.lines.map((line) => ({
    month: point.month, line_key: line.key, label: line.label, unit_cost: line.unit_cost, index: line.index,
    total_unit_cost: point.total_unit_cost, total_index: point.total_index, data_quality: point.data_quality,
    carried_sku_count: point.carried_sku_count, reference_inflation_pct: inflation, currency,
  })) : [{ month: point.month, total_unit_cost: point.total_unit_cost, total_index: point.total_index, data_quality: point.data_quality, carried_sku_count: point.carried_sku_count, reference_inflation_pct: inflation, currency }]));
}

export const fulfilmentIndexSkuCsv = (data: FulfilmentIndexData) => fulfilmentIndexRankingCsv(data, "skus");
export const fulfilmentIndexPlaceCsv = (data: FulfilmentIndexData) => fulfilmentIndexRankingCsv(data, "places");
export const fulfilmentIndexLineCsv = (data: FulfilmentIndexData) => fulfilmentIndexRankingCsv(data, "lines");

export const FULFILMENT_INDEX_OTHER_COLUMNS = ["month", "status", "unit_cost", "sku_count", "units", "currency"] as const;

export function fulfilmentIndexOtherCsv(data: FulfilmentIndexData): string {
  const other = data.other_costs;
  return csv(FULFILMENT_INDEX_OTHER_COLUMNS, (other?.series ?? []).map((point) => ({ ...point, currency: other?.currency ?? "" })));
}

export function fulfilmentIndexFilename(kind: FulfilmentRankingKind | "series" | "other", data: Pick<FulfilmentIndexData, "window">): string {
  return `pfc-fulfilment-index-${kind}-${data.window.from}-${data.window.to}.csv`;
}

// Keep these types visibly tied to the API rows: they are intentionally exported as flat ranking CSVs.
export type FulfilmentRankingRow = FulfilmentIndexSkuClimber | FulfilmentIndexPlaceClimber | FulfilmentIndexData["climbers"][number];
